import type { Editor } from "tinymce";

/**
 * Makes a started editor announce itself the way a plain field with a placeholder and a label does.
 * TinyMCE sets `data-mce-placeholder` (the hint text, which the suite's stylesheet draws) on the empty
 * editable and nothing for a screen reader. So once the editor has started, an editable whose host carries
 * a hint or a label id becomes a multi-line textbox, the only role `aria-placeholder` is valid on. It carries
 * `aria-placeholder` exactly while TinyMCE shows the hint, and `aria-labelledby` pointing at the label element.
 * The server renders none of these: before the editor starts, and after a failed download, the element takes
 * no typing and must not be announced as a text box. An editor with neither gets no role or aria attribute at all.
 */
export function announceEditor(editor: Editor, placeholder: string, labelledBy: string): void {
    if (!placeholder && !labelledBy) return;
    editor.on("init", () => {
        editor.getElement().setAttribute("role", "textbox");
        editor.getElement().setAttribute("aria-multiline", "true");
        if (labelledBy) editor.getElement().setAttribute("aria-labelledby", labelledBy);
    });
    if (!placeholder) return;
    editor.on("PlaceholderToggle", ({ state }) => {
        if (state) editor.getElement().setAttribute("aria-placeholder", placeholder);
        else editor.getElement().removeAttribute("aria-placeholder");
    });
}
